/**
 * Reattach session for RunView's fresh-load path. The package's
 * `usePlaygroundStream` hook intentionally doesn't expose `seedAwaiting`, so to
 * hydrate a suspended run on a cold page (different device / after refresh) we
 * own a `PlaygroundSession` directly and mirror it into React with
 * `useSyncExternalStore` — the same pattern the package hook uses internally,
 * plus a `seedAwaiting` passthrough.
 *
 * StrictMode note: on unmount we `cancel()` (abort in-flight work) rather than
 * `dispose()`, so the re-mount under StrictMode can still drive `respond()` —
 * matching the package hook's lifecycle.
 */
import { PlaygroundSession, type RespondInput, type SessionState, type StreamingAwaiting } from '@evt/flow-stream-ui'
import { useCallback, useEffect, useRef, useSyncExternalStore } from 'react'
import { playgroundTransport } from '../api/portal-client'

export interface ReattachSession {
  state: SessionState
  respond: (input: RespondInput) => Promise<void>
  cancel: () => void
  seedAwaiting: (executionId: string, awaiting: StreamingAwaiting['awaiting'], round?: number) => void
}

export function useReattachSession(): ReattachSession {
  const ref = useRef<PlaygroundSession | null>(null)
  if (!ref.current) {
    ref.current = new PlaygroundSession({
      executeFn: playgroundTransport.execute,
      resumeFn: playgroundTransport.resume
    })
  }
  const session = ref.current

  const subscribe = useCallback((cb: () => void) => session.subscribe(() => cb()), [session])
  const getSnapshot = useCallback(() => session.getState(), [session])
  const state = useSyncExternalStore(subscribe, getSnapshot, getSnapshot)

  useEffect(() => {
    return () => session.cancel()
  }, [session])

  const respond = useCallback((input: RespondInput) => session.respond(input), [session])
  const cancel = useCallback(() => session.cancel(), [session])
  const seedAwaiting = useCallback(
    (executionId: string, awaiting: StreamingAwaiting['awaiting'], round?: number) =>
      session.seedAwaiting(executionId, awaiting, round),
    [session]
  )

  return { state, respond, cancel, seedAwaiting }
}
